import { MONTHS, daysOfMonth } from "@/pages/texting/constants"
import { Placeholder, Team, TestMessagePayload, TextFormValues, Texting } from "@/pages/texting/types"
import { IManagePlanFormProps } from "@/pages/types/forms"
import { Credits, Placeholder as DjangoIOPlaceholder } from "@/types/djangio"
import type { QueryFunctionContext } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"
import { useCallback } from "react"

const { BulkSMS } = DjangIO.app.texting.models
const { Team: TeamModel } = DjangIO.app.userdata.models
const { Organization } = DjangIO.app.userdata.models
const { CreditsAutoRenewType, CreditsRenewCycle } = DjangIO.app.userdata.types

export const textingKeys = {
    all: [{ _model: "bulksms" }] as const,
    detail: (textId: string) => [{ ...textingKeys.all[0], textId }] as const,
    filters: (filters: ConstructorParameters<typeof URLSearchParams>[0]) => [...textingKeys.all, { filters }] as const,
}

export const fetchTeams = async (): Promise<Team[]> => {
    const response = await TeamModel.objects
        .filter({ default_for_user: true, organization: Userdata.organization_id })
        .GET()

    return response?.objects || []
}

export const fetchOrgPhone = async (): Promise<string> => {
    return await BulkSMS.objects.action("get_organization_send_phone").GET()
}

export const useOrgPhone = () => {
    return useQuery({
        queryKey: ["orgPhone"],
        queryFn: fetchOrgPhone,
        select: useCallback((data) => data?.phone_number, []),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        retry: 2,
    })
}

export const saveText = async (values: Texting, textId?: number | null): Promise<Texting> => {
    if (textId) {
        return await BulkSMS.objects
            .get(textId)
            .update(formValues2Text(values as any))
            .PATCH()
    }

    return await BulkSMS.objects.create(formValues2Text(values as any)).POST()
}

export const sendBulkSMS = async (textId: number): Promise<void> => {
    await BulkSMS.objects.get(textId).action("send_bulksms").GET()
}
export const deleteBulkSMS = async (textId: number): Promise<void> => {
    await BulkSMS.objects.get(textId).action("delete_sms").GET()
}

const fetchCredits = async (organizationId: string, excludeBulkSmsId?: string): Promise<Credits> => {
    return await Organization.objects
        .get(organizationId)
        .action("get_credits")
        .filter({ exclude_bulk_sms_id: excludeBulkSmsId })
        .GET()
}

const selectCredits = (credits: Credits): Credits => ({
    ...credits,
    balance: credits.balance ?? 0,
})

export const useCredits = (organizationId: string, excludeBulkSmsId?: string) => {
    return useQuery({
        queryKey: ["credits", organizationId, excludeBulkSmsId],
        queryFn: () => fetchCredits(organizationId, excludeBulkSmsId),
        select: useCallback(selectCredits, []),
        refetchOnWindowFocus: false,
    })
}

export const organizationPlanCredits2FormValues = (data: Credits): IManagePlanFormProps => {
    if (data.sms_renew_type === CreditsAutoRenewType.disabled.value)
        return {
            credits_renew_cycle_month: MONTHS[0].value,
            credits_renew_cycle_day: daysOfMonth[0].value,
            auto_renew_type: data.sms_renew_type.toString(),
            credits_renew_cycle_type: CreditsRenewCycle.month.value.toString(),
            credits_amount: data?.sms_limit,
        }

    if (!data?.sms_renew_date) return null

    const { month, day } = extractMonthDay(data.sms_renew_date)

    return {
        credits_renew_cycle_day: day.toString(),
        credits_renew_cycle_month: month.toString(),
        auto_renew_type: data?.sms_renew_type.toString(),
        credits_renew_cycle_type: data?.sms_renew_cycle.toString(),
        credits_amount: data?.sms_limit,
    }
}

export const getBulkSmsPlaceholders = async (): Promise<DjangoIOPlaceholder[]> => {
    return await BulkSMS.objects.action("get_placeholders_for_bulksms").GET()
}

export const formatPlaceholders = (placeholders: DjangoIOPlaceholder[]): Placeholder[] => {
    return placeholders.map((placeholder) => {
        return {
            group: placeholder.name,
            items: placeholder.items.map((item) => {
                return {
                    label: item.name,
                    value: item.value,
                }
            }),
        }
    })
}

export const usePlaceholders = () => {
    return useQuery({
        queryKey: ["textingPlaceholders"],
        queryFn: getBulkSmsPlaceholders,
        select: useCallback((placeholders) => formatPlaceholders(placeholders), []),
        staleTime: Infinity,
    })
}

export const BulkSMSIconsPlaceholdersGroup = {
    "Contact Placeholders": {
        name: "user-circle",
        family: "far",
    },
    "Contacts Custom Fields": {
        name: "user-edit",
        family: "far",
    },
    "Footprint Data": {
        name: "building",
        family: "far",
    },
    "Grassroots Links for Contacts": {
        name: "link",
        family: "far",
    },
}

export const mapIconToPlaceholderGroup = (group: string, iconsList) => {
    return iconsList[group]
}

export const fetchText = async ({
    queryKey,
}: QueryFunctionContext<ReturnType<(typeof textingKeys)["detail"]>>): Promise<Texting> => {
    const [{ textId }] = queryKey

    return await BulkSMS.objects.get(textId).GET()
}

export const useTexting = (textId: string) =>
    useQuery({
        queryKey: textingKeys.detail(textId),
        queryFn: fetchText,
        enabled: Boolean(textId),
        select: useCallback((text) => text2FormValues(text), []),
        staleTime: Infinity,
    })

const text2FormValues = (text: Texting): TextFormValues => {
    const [date, time] = buildDateAndTime(text.scheduled_time)
    return {
        description: text.description,
        text: text.text,
        limited_to_teams: text.limited_to_teams?.map((team) => TeamModel.idFromResourceUri(team).toString()) || [],
        permissions: text?.limited_to_teams.length > 0 ? 1 : 0,
        recipient_selection: text.recipient_selection,
        recipient_count: text.recipient_count,
        media_urls: text.media_urls,
        scheduled_time: text.scheduled_time,
        current_status: text.current_status,
        date,
        time,
    }
}

const formValues2Text = (values: TextFormValues): Texting => ({
    description: values.description,
    text: values.text,
    limited_to_teams: values.limited_to_teams?.map((team) => TeamModel.resourceUriFromId(team)) || [],
    recipient_selection: values.recipient_selection,
    recipient_count: values.recipient_count,
    media_urls: values.media_urls,
    scheduled_time: values.scheduled_time,
})

export const buildScheduledTime = (date: string | null, time: string | null): string => {
    if (!date || !time) return null

    const dateInstance = new Date(date)
    const [hours, minutes] = time.split(":")

    dateInstance.setHours(parseInt(hours))
    dateInstance.setMinutes(parseInt(minutes))
    dateInstance.setSeconds(0)

    return new Intl.DateTimeFormat("en-us", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h24",
    }).format(dateInstance)
}

const buildDateAndTime = (scheduledTime: string): [string | null, string | null] => {
    if (!scheduledTime) return [null, null]

    const [date, time] = scheduledTime.split("T")
    return [`${date} 12:00:00`, time.slice(0, 5)]
}

export const sendTestSmsPost = (payload: TestMessagePayload) => {
    return BulkSMS.objects.action("send_test_sms").update(payload).PATCH()
}

export function extractMonthDay(quorumDateString: string): { month: number; day: number } | null {
    const verifyQuorumDateTimeFieldFormat = /^(\d{4})-(\d{2})-(\d{2})$/
    const match = quorumDateString.match(verifyQuorumDateTimeFieldFormat)

    if (!match) return null

    const month = parseInt(match[2], 10)
    const day = parseInt(match[3], 10)

    return { month, day }
}

export const updateCreditsPlan = async (data: IManagePlanFormProps, organizationId: string) => {
    const parsedData = {}
    Object.entries(data).forEach(([key, val]) => {
        let value = parseInt(val)
        if (key === "credits_renew_cycle_month") value++ // Adjusting month index due to compatibility with python month index
        parsedData[key] = value
    })

    return await Organization.objects.get(organizationId).action("update_credits_plan").update(parsedData).PATCH()
}

export const addStandaloneCredits = async (organizationId: number, credits: number) => {
    return await Organization.objects.get(organizationId).action("add_additional_credits").update({ credits }).PATCH()
}
